// src/components/whatsapp/WhatsAppRequestPanel.tsx
// Panel reusable para las pantallas de WhatsApp cuando el negocio NO puede usar WhatsApp aún.
// Renderiza el estado correcto del modelo "plan único + WhatsApp bajo solicitud" (2026-06-25):
//   'free'    -> CTA "Mejorar a PRO"
//   'request' -> CTA "Solicitar activación de WhatsApp"
//   'pending' -> "Tu solicitud está en proceso"
// (el estado 'enabled' no usa este panel: la página muestra su contenido normal)
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Clock, MessageCircle, ArrowRight, ShieldCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { requestWhatsappActivation } from '../../services/whatsappRequestService';
import type { WhatsappAccessState } from '../../utils/whatsappAccess';

interface WhatsAppRequestPanelProps {
  state: WhatsappAccessState;
  /** Nombre de la función (ej. "Mensaje de Bienvenida") para personalizar el texto. */
  feature: string;
  description?: string;
}

const WhatsAppRequestPanel: React.FC<WhatsAppRequestPanelProps> = ({ state, feature, description }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [requested, setRequested] = useState(false);

  const handleRequest = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      await requestWhatsappActivation(currentUser);
      setRequested(true);
      toast.success('¡Solicitud enviada! Te activamos WhatsApp muy pronto 🙌');
    } catch (e) {
      console.error('[WhatsApp] error al solicitar activación:', e);
      toast.error('No se pudo enviar la solicitud. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const isPending = state === 'pending' || requested;

  return (
    <div className="bg-surface shadow rounded-lg p-8 text-center">
      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-500/20 dark:to-teal-500/20 mb-4">
        {state === 'free' ? (
          <Crown className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
        ) : isPending ? (
          <Clock className="h-8 w-8 text-amber-500" />
        ) : (
          <MessageCircle className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
        )}
      </div>

      {state === 'free' ? (
        <>
          <h2 className="text-lg font-semibold text-content mb-2">Función PRO</h2>
          <p className="text-sm text-content-2 mb-6 max-w-md mx-auto">
            {description || `${feature} es parte del plan PRO de Lealify.`}
          </p>
          <button
            onClick={() => navigate('/dashboard/subscription')}
            className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-md text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-md"
          >
            <Crown className="h-5 w-5 mr-2" />
            Mejorar a PRO
            <ArrowRight className="ml-2 h-4 w-4" />
          </button>
        </>
      ) : isPending ? (
        <>
          <h2 className="text-lg font-semibold text-content mb-2">Tu solicitud está en proceso 🕒</h2>
          <p className="text-sm text-content-2 max-w-md mx-auto">
            Estamos preparando tu conexión de WhatsApp con su propia línea dedicada para proteger tu
            número. Te avisamos en cuanto quede lista para activar <strong>{feature}</strong>.
          </p>
        </>
      ) : (
        <>
          <h2 className="text-lg font-semibold text-content mb-2">Activa WhatsApp para tu negocio</h2>
          <p className="text-sm text-content-2 mb-4 max-w-md mx-auto">
            {feature} se envía desde tu propio WhatsApp. Para cuidar tu número de bloqueos te damos una
            <strong> conexión dedicada solo tuya</strong>. La preparamos cuando nos lo pidas.
          </p>
          <div className="flex items-center justify-center gap-1.5 text-xs text-content-3 mb-6">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
            Envío espaciado y seguro — sin riesgo de baneo
          </div>
          <button
            onClick={handleRequest}
            disabled={loading}
            className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-md text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-md disabled:opacity-60"
          >
            <MessageCircle className="h-5 w-5 mr-2" />
            {loading ? 'Enviando…' : 'Solicitar activación de WhatsApp'}
          </button>
        </>
      )}
    </div>
  );
};

export default WhatsAppRequestPanel;
